/**
 * Audio Processing Hook
 * Handles audio decoding, WAV conversion, and video frame extraction
 */

import { useCallback, useRef, useEffect } from 'react';
import { AUDIO_CONFIG } from '../config/constants';
import { AudioProcessingResult } from '../types';

/**
 * Convert Uint8Array to base64 string
 */
function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = '';
  const len = bytes.byteLength;
  const chunk = 8192;
  for (let i = 0; i < len; i += chunk) {
    binary += String.fromCharCode.apply(
      null,
      bytes.subarray(i, i + chunk) as unknown as number[]
    );
  }
  return btoa(binary);
}

export function useAudioProcessing() {
  const audioContextRef = useRef<AudioContext | null>(null);

  /**
   * Get or create AudioContext instance
   */
  const getAudioContext = useCallback((): AudioContext => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)({
        sampleRate: AUDIO_CONFIG.SAMPLE_RATE,
      });
    }
    return audioContextRef.current;
  }, []);

  /**
   * Decode audio blob to AudioBuffer
   */
  const decodeAudio = useCallback(
    async (blob: Blob): Promise<AudioProcessingResult> => {
      const ctx = getAudioContext();
      const arrayBuffer = await blob.arrayBuffer();
      const buffer = await ctx.decodeAudioData(arrayBuffer);
      return { buffer, duration: buffer.duration };
    },
    [getAudioContext]
  );

  /**
   * Convert audio buffer slice to WAV base64
   */
  const sliceToWavBase64 = useCallback(
    (buffer: AudioBuffer, start: number, end: number): string => {
      const sampleRate = buffer.sampleRate;
      const startOffset = Math.floor(start * sampleRate);
      const endOffset = Math.floor(Math.min(end * sampleRate, buffer.length));
      const length = endOffset - startOffset;
      const channelData = buffer.getChannelData(0).subarray(startOffset, endOffset);

      const wavBuffer = new ArrayBuffer(44 + length * 2);
      const view = new DataView(wavBuffer);

      const writeString = (offset: number, s: string) => {
        for (let i = 0; i < s.length; i++) {
          view.setUint8(offset + i, s.charCodeAt(i));
        }
      };

      // WAV header
      writeString(0, 'RIFF');
      view.setUint32(4, 32 + length * 2, true);
      writeString(8, 'WAVE');
      writeString(12, 'fmt ');
      view.setUint32(16, 16, true);
      view.setUint16(20, 1, true);
      view.setUint16(22, 1, true);
      view.setUint32(24, sampleRate, true);
      view.setUint32(28, sampleRate * 2, true);
      view.setUint16(32, 2, true);
      view.setUint16(34, 16, true);
      writeString(36, 'data');
      view.setUint32(40, length * 2, true);

      // Audio data
      let offset = 44;
      for (let i = 0; i < channelData.length; i++, offset += 2) {
        const s = Math.max(-1, Math.min(1, channelData[i]));
        view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
      }

      return uint8ArrayToBase64(new Uint8Array(wavBuffer));
    },
    []
  );

  /**
   * Extract video frames at specified timestamps
   */
  const extractFrames = useCallback(
    async (blob: Blob, timestamps: number[]): Promise<string[]> => {
      if (!blob.type.startsWith('video/')) {
        return [];
      }

      return new Promise((resolve) => {
        const video = document.createElement('video');
        const objectUrl = URL.createObjectURL(blob);
        video.src = objectUrl;
        video.muted = true;
        const frames: string[] = [];

        video.onloadeddata = async () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          for (const timestamp of timestamps) {
            await new Promise<void>((r) => {
              video.currentTime = Math.min(timestamp, video.duration);
              video.onseeked = () => {
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                ctx?.drawImage(video, 0, 0);
                const data = canvas
                  .toDataURL('image/jpeg', AUDIO_CONFIG.FRAME_QUALITY)
                  .split(',')[1];
                if (data) frames.push(data);
                r();
              };
            });
          }
          URL.revokeObjectURL(objectUrl);
          resolve(frames);
        };

        video.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          resolve([]);
        };
      });
    },
    []
  );

  /**
   * Cleanup AudioContext on unmount
   */
  const cleanup = useCallback(() => {
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(console.error);
      audioContextRef.current = null;
    }
  }, []);

  // Auto-cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    decodeAudio,
    sliceToWavBase64,
    extractFrames,
    cleanup,
    getAudioContext,
  };
}
